<template lang="pug">
div.comment-body(ref="root")
  CommentBodySummary(
    v-if="bodySummary"
    :title="i18n.t('TheComment.InShort')"
  )
    div(v-html="bodySummary")

  TableOfContents(
    v-if="toc.length"
    :title="i18n.t('TheComment.Contents')"
    :items="toc"
  )

  CommentBodyPart(
    v-for="part in bodyFullParts"
    :key="part.key"
    :part="part"
  )
</template>

<script setup lang="ts">
import { inject } from "vue";
import { parseVidsString, parseWordOrdinals } from "~/units/scripture";
import { useI18n } from "vue-i18n";
import CommentBodySummary from "~/components/CommentBodySummary.vue";
import TableOfContents from "~/components/TableOfContents.vue";
import { useVersePreviewStore } from "~/stores/useVersePreviewStore";
import { scrollIfAnchor } from "~/units/browser";
import type { Props } from "~/components/CommentBodyFull.types";
import { InModalViewKey } from "~/injectionKeys";

const props = withDefaults(defineProps<Props>(), {});

const i18n = useI18n();
const inModalComment = inject(InModalViewKey, false);

const emit = defineEmits<{
  showNote: [n: number];
}>();

const root = ref<HTMLElement | null>(null);

const versePreviewStore = useVersePreviewStore();

function onClickScriptureReference(event: Event) {
  const el = event.currentTarget;
  if (!(el instanceof HTMLElement)) {
    return;
  }

  if (!el.dataset.vids) {
    return;
  }

  const range = parseVidsString(el.dataset.vids, el.dataset.tid ?? null)[0];
  if (!range) {
    return;
  }

  range.tr = range.tid ? el.dataset.tr : undefined;
  range.wordOrdinals = parseWordOrdinals(el.dataset.wordOrdinals ?? null);
  event.preventDefault();
  versePreviewStore.preview(range, inModalComment);
}

function onClickNote(event: Event) {
  const el = event.target;
  if (!(el instanceof HTMLElement)) {
    return;
  }

  if (!el.dataset.note) {
    return;
  }
  const n = parseInt(el.dataset.note);

  event.preventDefault();
  emit("showNote", n);
}

function onClickToc(event: Event) {
  const el = event.target;

  if (!(el instanceof HTMLElement)) {
    return;
  }

  event.preventDefault();
  const href =
    el.getAttribute("href") ?? el.parentElement?.getAttribute("href") ?? "#";
  scrollIfAnchor(href);
}

function safelyAddClickEventListnerOnContent() {
  // Make scripture references clickable.
  root.value?.querySelectorAll(".scripture-reference").forEach((el) => {
    el.removeEventListener("click", onClickScriptureReference);
    el.addEventListener("click", onClickScriptureReference);
  });

  // Make notes clickable.
  root.value?.querySelectorAll("sup > a[data-note]").forEach((el) => {
    el.removeEventListener("click", onClickNote);
    el.addEventListener("click", onClickNote);
  });

  root.value?.querySelectorAll("[data-toc-link]").forEach((el) => {
    el.removeEventListener("click", onClickToc);
    el.addEventListener("click", onClickToc);
  });
}

onMounted(safelyAddClickEventListnerOnContent);
onUpdated(safelyAddClickEventListnerOnContent);
</script>

<style lang="scss">
.scripture-reference {
  font-weight: bold;

  transition-duration: 0.3s;
  transition-property: background-color;

  text-decoration: none;
  cursor: pointer;

  &:hover {
    background-color: rgb(var(--v-theme-secondary-c100));

    .v-theme--dark & {
      background-color: rgb(var(--v-theme-primary-c800));
    }
  }
}
</style>
